// src/lib/rdv.ts
import type { GoogleEvent, ProspectStatus } from "@shared/schema";
import type { Tone } from "./colors";

export type RdvState = "upcoming" | "past" | "cancelled";

/**
 * An RDV is:
 *  - `cancelled` if its stored status is "cancelled"
 *  - `past` if it has an endTime and that endTime is in the past;
 *     if no endTime, fall back to startTime
 *  - `upcoming` otherwise (future or currently happening)
 */
export function getRdvState(
  evt: Pick<GoogleEvent, "status" | "startTime" | "endTime">,
  now: Date = new Date(),
): RdvState {
  if (evt.status === "cancelled") return "cancelled";
  const ref = evt.endTime ?? evt.startTime;
  if (!ref) return "upcoming";
  const refDate = new Date(ref);
  if (refDate.getTime() < now.getTime()) return "past";
  return "upcoming";
}

export function hasStarted(evt: Pick<GoogleEvent, "startTime">, now: Date): boolean {
  const t = new Date(evt.startTime).getTime();
  return !Number.isNaN(t) && t <= now.getTime();
}

/** Commencé (début <= maintenant) et sans issue : « À pointer » (l'annulé est écarté avant). */
function startedWithoutOutcome(
  evt: Pick<GoogleEvent, "startTime" | "outcome">,
  now: Date,
): boolean {
  return !evt.outcome && hasStarted(evt, now);
}

export const RDV_TONE: Record<RdvState, Tone> = {
  upcoming: "sky",
  past: "yellow",
  cancelled: "slate",
};

export const RDV_LABEL: Record<RdvState, string> = {
  upcoming: "RDV à venir",
  past: "RDV passé",
  cancelled: "RDV annulé",
};

/**
 * Visual treatment of an RDV, combining its time-state, its recorded outcome
 * (présent / no-show), and the linked prospect's status. The prospect's
 * outcome (gagné / perdu) wins over the time-state so closing a prospect
 * instantly recolours its RDV everywhere it's shown. Precedence:
 *   1. annulé              → gris (slate)
 *   2. prospect gagné      → vert (closed_won)
 *   3. prospect perdu      → rouge (closed_lost)
 *   4. commencé sans issue → corail (coral, « À pointer »)
 *   5. à venir             → bleu (sky)
 *   6. absent              → gris (slate)
 *   7. présent             → jaune (yellow)
 */
export function getRdvTone(
  evt: Pick<GoogleEvent, "status" | "startTime" | "endTime" | "outcome">,
  prospectStatus?: ProspectStatus | null,
  now: Date = new Date(),
): Tone {
  const state = getRdvState(evt, now);
  if (state === "cancelled") return "slate";
  if (prospectStatus === "closed_won") return "green";
  if (prospectStatus === "closed_lost") return "red";
  if (startedWithoutOutcome(evt, now)) return "coral";
  if (state === "upcoming" && !hasStarted(evt, now)) return "sky";
  if (evt.outcome === "no_show") return "slate";
  return "yellow";
}

export function getRdvLabel(
  evt: Pick<GoogleEvent, "status" | "startTime" | "endTime" | "outcome">,
  prospectStatus?: ProspectStatus | null,
  now: Date = new Date(),
): string {
  const state = getRdvState(evt, now);
  if (state === "cancelled") return "RDV annulé";
  if (prospectStatus === "closed_won") return "Client gagné";
  if (prospectStatus === "closed_lost") return "Perdu";
  if (startedWithoutOutcome(evt, now)) return "À pointer";
  if (state === "upcoming" && !hasStarted(evt, now)) return "RDV à venir";
  if (evt.outcome === "no_show") return "Absent";
  return "Présent";
}
